/**
 * @file Main.jsx
 * @description Main page of the app (renders base page with nav bars)
 *
 * @version 1.0.0
 * @author Carmelino Galang
 * @copyright Copyright (c) 2024, NetCyberOps LLC
 */
import React, {Suspense, useContext, useEffect, useRef, useState} from "react";
import {Outlet} from "react-router-dom";
import * as Sentry from "@sentry/react";
import ChunkErrorFallback from "@components/ChunkErrorFallback/ChunkErrorFallback.jsx";
import {Container, Spinner} from "react-bootstrap";
import AppToast from "@components/AppToast/AppToast.jsx";

import {KeycloakContext} from "@context/KeycloakContext";
import {useSidebar} from "@context/SidebarContext";
import {useWebSocket} from "@context/WebSocketContext.jsx";
import SideNav from "./SideNav/SideNav";
import TopNav from "./TopNav/TopNav";
import FormConfirmationModal from "@modals/FormConfirmationModal/FormConfirmationModal.jsx";
import NoAccess from "@sections/NoAccess/NoAccess.jsx";

export default function Main() {
    const {user, userType, authenticated, userFeatures, userPermissions, hasNoAccess, token, logout} =
        useContext(KeycloakContext);
    const {joinRoom, addEventListener} = useWebSocket();

    const [signingOut, setSigningOut] = useState(false);
    const {isExpanded, screenWidth} = useSidebar();

    const [version, setVersion] = useState(null)
    const [showRefreshModal, setShowRefreshModal] = useState(false);

    const versionRef = useRef(version);
    useEffect(() => {
        versionRef.current = version;
    }, [version]);

    const timeoutRef = useRef(null);

    useEffect(() => {
        const element = document.querySelector('script[type="module"][src^="/assets/index-"][src$=".js"]');
        if (element) {
            const src = element.getAttribute('src');
            const match = src.match(/index-(.+)\.js$/);
            if (match) setVersion(match[1]);
        }
    }, []);

    useEffect(() => {
        const handleIdle = () => logout();

        const resetTimer = () => {
            clearTimeout(timeoutRef.current);
            // 8hrs for employees, 2hrs for partners or customers
            const idle_timeout = userType === "employee" ? 8 * 60 * 60 * 1000 : 2 * 60 * 60 * 1000;

            timeoutRef.current = setTimeout(() => {
                handleIdle();
            }, idle_timeout);
        };

        const events = ["click", "keydown", "scroll"];

        events.forEach((event) => {
            window.addEventListener(event, resetTimer);
        });

        resetTimer();

        return () => {
            clearTimeout(timeoutRef.current);

            events.forEach((event) => {
                window.removeEventListener(event, resetTimer);
            });
        };
    }, [userType]);


    // Subscribe to the 'general' room and listen for version updates
    useEffect(() => {
        // Guard clause - only proceed if we have authentication and features
        if (!user || !authenticated || userFeatures === null || userPermissions === null || hasNoAccess) return;

        // Join the general room
        joinRoom('general::');

        // Listen for version updates
        const removeVersionUpdateListener = addEventListener('webversion_updated', (data) => {
            // console.log('Received webversion_updated event', data);
            const {resource_id} = data;
            // console.log("Most up to date version", resource_id, "Current version", versionRef.current, resource_id !== versionRef.current);
            console.log(`${resource_id !== versionRef.current ? "Refreshing" : "Not refreshing"} dashboard due to version update: ${resource_id} -> ${versionRef.current} = ${resource_id !== versionRef.current}`)

            // If version is different, show refresh modal
            if (resource_id !== versionRef.current) {
                setShowRefreshModal(true);
                setVersion(resource_id);
            }
        });

        // Cleanup: remove the event listener when component unmounts
        return () => {
            // console.log("Unsubscribing from general room");
            // removeVersionUpdateListener();
            removeEventListener('webversion_updated', removeVersionUpdateListener);
        };
    }, [user, authenticated, userFeatures, userPermissions, hasNoAccess]);

    // render loading screen in user info not completed loading
    if (
        !user ||
        !authenticated ||
        userFeatures === null ||
        userPermissions === null
    ) {
        return (
            <Container
                className="p-0 h-100 d-flex justify-content-center align-items-center"
                fluid
                id="dash-wrapper"
            >
                <h4 className="pt-5 d-flex justify-content-center text-white">
                    <Spinner animation="border" role="status"/>
                    &nbsp;&nbsp; Loading...
                </h4>
            </Container>
        );
    }

    if (hasNoAccess) {
        return (
            <Container className="p-0" fluid id="dash-wrapper">
                <NoAccess/>
            </Container>
        );
    }

    return (
        <Container className="p-0" fluid id="dash-wrapper">
            <FormConfirmationModal
                showConfirmationModal={showRefreshModal}
                handleCloseConfirmationModal={() => setShowRefreshModal(false)}
                message={`The dashboard has been updated. Do you want to reload the page to get the latest version.`}
                handleSubmit={() => window.location.reload()}
            />
            {signingOut ? (
                <h4 className="pt-5 d-flex justify-content-center text-white ass">
                    <Spinner animation="border" role="status"/>
                    &nbsp;&nbsp; Signing out...
                </h4>
            ) : (
                <div id="content-wrapper">
                    <SideNav
                        user={user}
                        userType={userType}
                        userPermissions={userPermissions}
                        userFeatures={userFeatures}
                    />
                    <div
                        id="main"
                        className={`sku_height${
                            screenWidth < 1280 && isExpanded ? " topNav-expand" : ""
                        }`}
                    >
                        <TopNav
                            setSigningOut={setSigningOut}
                        />
                        <Sentry.ErrorBoundary fallback={ChunkErrorFallback}>
                            {/* Hidden marker while lazy code loads: the e2e tests wait for it to go away. */}
                            <Suspense fallback={<span hidden data-suspense-pending/>}><Outlet/></Suspense>
                        </Sentry.ErrorBoundary>
                        <AppToast/>
                    </div>
                </div>
            )}
        </Container>
    );
}
